import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BottomNavBar from './BottomNavBar';
import ChatsDrawer from './ChatsDrawer';
import SpacesDrawer from './SpacesDrawer';
import CreateSpaceModal from './CreateSpaceModal';
import FeedView from '../views/FeedView';
import SettingsView from '../views/SettingsView';
import NotificationsView from '../views/NotificationsView';
// Phase 1: ChatView import removed - all chats use RealChatView (defined inline below)
import { initializeGhostData } from '@/utils/AdvancedDataSeeder';
import { useSimulation } from '@/hooks/useSimulation';
import { useFeed } from '@/hooks/useFeed';
import { useSpaces } from '@/hooks/useSpaces';
// Real friend/chat/call hooks
import { useDMMessages } from '@/hooks/useDMMessages';
import { useFriendCall } from '@/hooks/useFriendCall';
import { useFriendRequests } from '@/hooks/useFriendRequests';
import { supabase } from '@/lib/supabase';
import { searchUsersForFriends } from '@/lib/search-users';
import CallOverlay from '../call/CallOverlay';

/**
 * MobileLayout - Premium mobile navigation wrapper
 *
 * Features:
 * - Persistent bottom navigation bar
 * - Floating drawers for Chats and Spaces
 * - Manages navigation state
 * - Routes integration
 * - Unread counts and badges
 *
 * @param {ReactNode} children - Page content
 * @param {object} user - Current user object
 * @param {object} profile - User profile
 */
export default function MobileLayout({ children, user, profile }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation state
  const [activeTab, setActiveTab] = useState('feed');
  const [currentView, setCurrentView] = useState('feed'); // 'feed' | 'notifications' | 'settings' | 'chat'
  const [drawerOpen, setDrawerOpen] = useState(null); // 'chats' | 'spaces' | null
  const [selectedChat, setSelectedChat] = useState(null); // Selected chat for ChatView

  // Data state (seeded with Ghost Data)
  const [ghostUsers, setGhostUsers] = useState([]); // Ghost Users for simulation
  const [contentBuffer, setContentBuffer] = useState([]); // Backstage buffer for new posts
  const [ghostFeed, setGhostFeed] = useState([]); // Ghost feed for fallback
  const [chats, setChats] = useState([]);
  const [ghostSpaces, setGhostSpaces] = useState([]); // Ghost spaces for fallback
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Bug #16/25 Fix: Use real feed from database with ghost fallback
  const {
    feed: realFeed,
    isLoading: feedLoading,
    isGhostData,
    refresh: refreshFeed,
  } = useFeed({
    limit: 20,
    realtime: true,
    ghostFallback: ghostFeed,
  });

  // Use real feed when available, ghost feed as fallback
  const feed = realFeed.length > 0 ? realFeed : ghostFeed;

  // Bug #21 Fix: Use real spaces from database with ghost fallback
  const {
    spaces: realSpaces,
    isLoading: spacesLoading,
    isGhostData: spacesIsGhostData,
    refresh: refreshSpaces,
    joinSpace,
    leaveSpace,
    createSpace,
  } = useSpaces({
    ghostFallback: ghostSpaces,
    realtime: true,
  });

  // Use real spaces when available, ghost spaces as fallback
  const spaces = realSpaces.length > 0 ? realSpaces : ghostSpaces;

  // Unread counts
  const [unreadCounts, setUnreadCounts] = useState({
    chats: 0,
    spaces: 0,
    notifications: 0
  });

  // Modal states
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(profile);

  // 🔥 REAL FRIEND FEATURES (on top of Ghost Data)
  const [friendSearchOpen, setFriendSearchOpen] = useState(false);
  const [friendSearchQuery, setFriendSearchQuery] = useState('');
  const [friendSearchResults, setFriendSearchResults] = useState([]);
  const [friendSearchLoading, setFriendSearchLoading] = useState(false);
  const [realDMChat, setRealDMChat] = useState(null); // For real DM with actual friend

  // Real DM messages hook
  const {
    conversations: realConversations,
    activeConversation,
    messages: realMessages,
    loading: dmLoading,
    loadConversations,
    getOrCreateConversation,
    setActiveConversation,
    loadMessages,
    sendMessage: sendRealMessage,
    markAsRead,
    subscribeToMessages,
    unsubscribeFromMessages,
  } = useDMMessages(user?.id || null);

  // Real friend call hook
  const {
    callState,
    activeCall,
    incomingCall,
    isMuted: callIsMuted,
    isDeafened: callIsDeafened,
    isVideoOn: callIsVideoOn,
    localStream: callLocalStream,
    remoteStream: callRemoteStream,
    callDuration,
    error: callError,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute: toggleCallMute,
    toggleDeafen: toggleCallDeafen,
    toggleVideo: toggleCallVideo,
  } = useFriendCall(
    user?.id || null,
    profile?.username || null,
    profile?.avatar_url || null
  );

  // Real friend requests hook
  const {
    friends: realFriends,
    pendingReceived,
    pendingSent,
    loading: friendsLoading,
    sendRequest,
    acceptRequest,
    rejectRequest,
    removeFriend,
    refreshFriends,
  } = useFriendRequests(user?.id || null);

  // Sync active tab with route
  useEffect(() => {
    const path = location.pathname;

    if (path.startsWith('/dm/')) {
      setActiveTab('chats');
    } else if (path.startsWith('/spaces/')) {
      setActiveTab('spaces');
    } else if (path === '/' || path === '/feed') {
      setActiveTab('feed');
    } else if (path === '/notifications') {
      setActiveTab('notifications');
    } else if (path.startsWith('/settings')) {
      setActiveTab('settings');
    }
  }, [location]);

  // 📱 ORIENTATION LOCK - Keep app in portrait mode
  useEffect(() => {
    // Lock to portrait if supported
    const lockOrientation = async () => {
      try {
        if (screen.orientation?.lock) {
          await screen.orientation.lock('portrait');
        }
      } catch (e) {
        // Silently fail - not all browsers support this
      }
    };
    lockOrientation();

    // Prevent zoom on double tap
    document.addEventListener('touchstart', (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    }, { passive: false });

    let lastTouchEnd = 0;
    document.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    }, { passive: false });
  }, []);

  // 🛡️ BACK BUTTON - Navigate within app, never leave
  useEffect(() => {
    // Push initial state to prevent leaving site on first back press
    if (!window.history.state?.gjylApp) {
      window.history.pushState({ gjylApp: true, view: 'feed' }, '', window.location.pathname);
    }

    const handlePopState = (event) => {
      // Always prevent default browser back behavior
      event.preventDefault?.();

      // Close modals/drawers first (in priority order)
      if (friendSearchOpen) {
        setFriendSearchOpen(false);
        window.history.pushState({ gjylApp: true, view: currentView }, '', window.location.pathname);
        return;
      }

      if (drawerOpen) {
        setDrawerOpen(null);
        window.history.pushState({ gjylApp: true, view: currentView }, '', window.location.pathname);
        return;
      }

      if (createSpaceOpen) {
        setCreateSpaceOpen(false);
        window.history.pushState({ gjylApp: true, view: currentView }, '', window.location.pathname);
        return;
      }

      // If in chat view, go back to feed
      if (currentView === 'chat' || currentView === 'realchat') {
        setCurrentView('feed');
        setSelectedChat(null);
        setRealDMChat(null);
        setActiveConversation(null);
        setActiveTab('feed');
        window.history.pushState({ gjylApp: true, view: 'feed' }, '', window.location.pathname);
        return;
      }

      // If in settings or notifications, go back to feed
      if (currentView === 'settings' || currentView === 'notifications') {
        setCurrentView('feed');
        setActiveTab('feed');
        window.history.pushState({ gjylApp: true, view: 'feed' }, '', window.location.pathname);
        return;
      }

      // Already on feed - just push state again to prevent leaving
      window.history.pushState({ gjylApp: true, view: 'feed' }, '', window.location.pathname);
    };

    // Also handle beforeunload for extra protection
    const handleBeforeUnload = (e) => {
      if (callState === 'connected' || callState === 'calling') {
        e.preventDefault();
        e.returnValue = 'You have an active call. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentView, drawerOpen, createSpaceOpen, friendSearchOpen, callState]);

  // 🔍 FRIEND SEARCH - Debounced search
  useEffect(() => {
    if (!friendSearchQuery.trim()) {
      setFriendSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setFriendSearchLoading(true);
      try {
        const results = await searchUsersForFriends(friendSearchQuery, 10);
        setFriendSearchResults(results || []);
      } catch (err) {
        console.error('Friend search error:', err);
        setFriendSearchResults([]);
      } finally {
        setFriendSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [friendSearchQuery]);

  // Load real conversations on mount
  useEffect(() => {
    if (user?.id) {
      loadConversations();
      subscribeToMessages();
    }
    return () => unsubscribeFromMessages();
  }, [user?.id]);

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Initialize Ghost Data on mount (used as fallback when no real posts exist)
  useEffect(() => {
    async function loadGhostData() {
      setLoading(true);

      try {
        console.log('[MobileLayout] Loading Ghost Data as fallback...');

        // Initialize Ghost Data (used as fallback)
        const ghostData = await initializeGhostData();

        // Set ghost data (feed/spaces hooks will use real data if available)
        setGhostFeed(ghostData.feed || []);
        setChats(ghostData.chats || []);
        setGhostSpaces(ghostData.spaces || []); // Bug #21: Ghost spaces fallback
        setNotifications(ghostData.notifications || []);

        // Save ghost users and buffer for simulation
        setGhostUsers(ghostData.users || []);
        setContentBuffer(ghostData.contentBuffer || []);

        console.log('[MobileLayout] Ghost Data ready as fallback');
        console.log(`  Buffer: ${ghostData.contentBuffer?.length || 0} posts ready`);
      } catch (error) {
        console.error('[MobileLayout] Failed to load Ghost Data:', error);
        // Set empty arrays on error
        setGhostFeed([]);
        setChats([]);
        setGhostSpaces([]);
        setNotifications([]);
        setGhostUsers([]);
        setContentBuffer([]);
      } finally {
        setLoading(false);
      }
    }

    loadGhostData();
  }, []);

  // Calculate unread counts (including pending friend requests)
  // Phase 0 fix: Use realConversations instead of ghost chats
  useEffect(() => {
    const chatUnread = realConversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
    const spaceUnread = spaces.reduce((sum, space) => sum + (space.unreadMentions || 0), 0);
    const notificationUnread = notifications.filter(n => !n.isRead).length;
    const friendRequestCount = pendingReceived?.length || 0;

    setUnreadCounts({
      chats: chatUnread + friendRequestCount, // Include friend requests in chat badge
      spaces: spaceUnread,
      notifications: notificationUnread + friendRequestCount // Also show in notifications
    });
  }, [realConversations, spaces, notifications, pendingReceived]);

  // Handle tab change - cleaner logic to prevent stuck states
  const handleTabChange = (tab) => {
    // First, always close any open drawer when switching to a non-drawer tab
    if (tab === 'feed' || tab === 'notifications' || tab === 'settings') {
      setDrawerOpen(null);
      setCurrentView(tab === 'feed' ? 'feed' : tab);
      setActiveTab(tab);
      // Reset body overflow in case it's stuck
      document.body.style.overflow = '';
      return;
    }

    // For drawer tabs (chats, spaces), toggle behavior
    if (tab === 'chats') {
      if (drawerOpen === 'chats') {
        // Close if already open
        setDrawerOpen(null);
      } else {
        // Open chats (this will close spaces if open)
        setDrawerOpen('chats');
      }
      setActiveTab(tab);
    } else if (tab === 'spaces') {
      if (drawerOpen === 'spaces') {
        // Close if already open
        setDrawerOpen(null);
      } else {
        // Open spaces (this will close chats if open)
        setDrawerOpen('spaces');
      }
      setActiveTab(tab);
    }
  };

  // Handle chat selection
  const handleChatSelect = (chatId) => {
    const chat = chats.find(c => c.id === chatId);
    if (chat) {
      setSelectedChat(chat);
      setCurrentView('chat');
      setDrawerOpen(null);
      setActiveTab('chats');

      // Push history state so back button works
      window.history.pushState({ view: 'chat', chatId }, '', window.location.pathname);
    }
  };

  // Handle back from chat
  const handleBackFromChat = () => {
    // Use history.back() to properly navigate back
    // The popstate handler will handle updating state
    if (window.history.state?.view === 'chat') {
      window.history.back();
    } else {
      // Fallback: manually reset state
      setCurrentView('feed');
      setSelectedChat(null);
      setActiveTab('feed');
    }
  };

  // Handle space selection
  const handleSpaceSelect = (spaceId) => {
    navigate(`/spaces/${spaceId}`);
  };

  // Handle add chat - Opens friend search
  const handleAddChat = () => {
    setDrawerOpen(null);
    setFriendSearchOpen(true);
    setFriendSearchQuery('');
    setFriendSearchResults([]);
  };

  // Start real DM with a friend
  const handleStartRealChat = async (friendUser) => {
    try {
      const conversationId = await getOrCreateConversation(friendUser.id);
      if (conversationId) {
        await loadMessages(conversationId);
        setRealDMChat(friendUser);
        setCurrentView('realchat');
        setFriendSearchOpen(false);
        setDrawerOpen(null);
        window.history.pushState({ gjylApp: true, view: 'realchat' }, '', window.location.pathname);
      }
    } catch (err) {
      console.error('Failed to start chat:', err);
    }
  };

  // Handle selecting a real conversation from drawer
  const handleRealConversationSelect = async (conv) => {
    if (conv.other_user) {
      setActiveConversation(conv);
      await loadMessages(conv.id);
      setRealDMChat(conv.other_user);
      setCurrentView('realchat');
      setDrawerOpen(null);
      window.history.pushState({ gjylApp: true, view: 'realchat' }, '', window.location.pathname);
    }
  };

  // Start a call with a friend
  const handleStartCall = async (friendId, friendName, friendAvatar, type = 'voice') => {
    try {
      await startCall(friendId, friendName, friendAvatar, type);
    } catch (err) {
      console.error('Failed to start call:', err);
    }
  };

  // Send friend request
  const handleSendFriendRequest = async (targetUserId) => {
    try {
      const result = await sendRequest(targetUserId);
      if (result.success) {
        // Only update UI if request succeeded
        setFriendSearchResults(prev => prev.map(u =>
          u.id === targetUserId ? { ...u, requestSent: true } : u
        ));
      } else {
        console.error('Friend request failed:', result.error);
        // Could add toast notification here
      }
    } catch (err) {
      console.error('Failed to send friend request:', err);
    }
  };

  // Handle add space
  const handleAddSpace = () => {
    setDrawerOpen(null);
    setCreateSpaceOpen(true);
  };

  // Handle space created/joined - refresh from database to get real data
  const handleSpaceCreated = async (newSpace) => {
    // If using ghost data, add to ghost spaces for immediate UI feedback
    if (spacesIsGhostData) {
      setGhostSpaces(prevSpaces => [newSpace, ...prevSpaces]);
    }
    // Refresh from database to get the real space
    await refreshSpaces();
  };

  // Handle profile update from settings
  const handleProfileUpdate = (updatedProfile) => {
    setCurrentProfile(updatedProfile);
  };

  // 🔗 REAL CONVERSATIONS ONLY (Ghost chats removed - Phase 0 fix)
  // Ghost users exist in DB but don't appear in inbox until user starts a conversation
  const mergedChats = useMemo(() => {
    const realChatsFormatted = realConversations.map(conv => ({
      id: `real-${conv.id}`,
      realId: conv.id,
      isReal: true,
      username: conv.other_user?.username || 'Unknown',
      display_name: conv.other_user?.username,
      avatar_url: conv.other_user?.avatar_url,
      lastMessage: conv.last_message || 'Start a conversation',
      lastMessageTime: conv.last_message_at ? new Date(conv.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
      unread: conv.unread_count || 0,
      other_user: conv.other_user,
      conversation: conv,
    }));
    // Phase 0: Only return real conversations - no ghost chats
    return realChatsFormatted;
  }, [realConversations]);

  // Handle chat selection - detect if real or ghost
  const handleMergedChatSelect = (chatId) => {
    const chat = mergedChats.find(c => c.id === chatId);
    if (!chat) return;

    if (chat.isReal && chat.conversation) {
      handleRealConversationSelect(chat.conversation);
    } else {
      handleChatSelect(chatId);
    }
  };

  // Simulation for ghost data (only runs when using ghost feed as fallback)
  const { stats, bufferRemaining } = useSimulation({
    users: ghostUsers,
    contentBuffer: contentBuffer,
    setFeed: setGhostFeed, // Update ghost feed for simulation
    setNotifications: setNotifications,
    setSpaces: setGhostSpaces, // Bug #21: Update ghost spaces for simulation
    setChats: setChats,
    enabled: !loading && isGhostData // Only run simulation when using ghost data
  });

  // 🔥 Loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gjyl-darker flex items-center justify-center">
        <div className="text-center max-w-md px-8">
          {/* Logo */}
          <img
            src="/logos/gjyl-logo-oauth-512.png"
            alt="GJYL"
            className="w-20 h-20 mx-auto mb-6 rounded-xl drop-shadow-2xl animate-pulse"
          />

          {/* Spinner */}
          <div className="w-8 h-8 mx-auto border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4"></div>

          {/* Text */}
          <p className="text-slate-400 text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gjyl-darker pb-16">
      {/* Simulation debug panel removed - was showing in production */}

      {/* Main content - render based on currentView */}
      <div className="h-full">
        {currentView === 'feed' && <FeedView feed={feed} />}
        {currentView === 'settings' && <SettingsView user={user} profile={currentProfile || profile} onProfileUpdate={handleProfileUpdate} />}
        {/* Phase 1: Ghost ChatView removed - all chats now use RealChatView */}
        {/* 🔥 REAL CHAT VIEW - For actual DMs with friends */}
        {currentView === 'realchat' && realDMChat && (
          <RealChatView
            friend={realDMChat}
            messages={realMessages}
            onBack={() => {
              setCurrentView('feed');
              setRealDMChat(null);
              setActiveConversation(null);
              setActiveTab('feed');
            }}
            onSend={async (content) => {
              await sendRealMessage(content);
            }}
            onCall={() => handleStartCall(realDMChat.id, realDMChat.username, realDMChat.avatar_url, 'voice')}
            onVideoCall={() => handleStartCall(realDMChat.id, realDMChat.username, realDMChat.avatar_url, 'video')}
            callDisabled={callState !== 'idle'}
          />
        )}
        {currentView === 'notifications' && <NotificationsView />}
      </div>

      {/* Bottom Navigation Bar */}
      <BottomNavBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        unreadCounts={unreadCounts}
      />

      {/* Chats Drawer - Shows both real and ghost chats + friend requests */}
      <ChatsDrawer
        isOpen={drawerOpen === 'chats'}
        onClose={() => setDrawerOpen(null)}
        chats={mergedChats}
        onChatSelect={handleMergedChatSelect}
        onAddChat={handleAddChat}
        loading={loading || dmLoading}
        pendingRequests={pendingReceived}
        onAcceptRequest={acceptRequest}
        onRejectRequest={rejectRequest}
      />

      {/* Spaces Drawer */}
      <SpacesDrawer
        isOpen={drawerOpen === 'spaces'}
        onClose={() => setDrawerOpen(null)}
        spaces={spaces}
        onSpaceSelect={handleSpaceSelect}
        onAddSpace={handleAddSpace}
        loading={loading}
      />

      {/* Create Space Modal */}
      <CreateSpaceModal
        isOpen={createSpaceOpen}
        onClose={() => setCreateSpaceOpen(false)}
        user={user}
        onSpaceCreated={handleSpaceCreated}
      />

      {/* 🔍 FRIEND SEARCH MODAL */}
      {friendSearchOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center">
          <div className="w-full max-w-lg bg-gjyl-dark rounded-t-3xl max-h-[85vh] flex flex-col animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">Find Friends</h2>
              <button
                onClick={() => setFriendSearchOpen(false)}
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white"
              >
                ✕
              </button>
            </div>

            {/* Search Input */}
            <div className="px-4 py-3">
              <input
                type="text"
                placeholder="Search by username..."
                value={friendSearchQuery}
                onChange={(e) => setFriendSearchQuery(e.target.value)}
                autoFocus
                className="w-full h-12 px-4 bg-zinc-800 text-white rounded-xl placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              />
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto px-4 pb-8">
              {friendSearchLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
                </div>
              ) : friendSearchResults.length === 0 && friendSearchQuery ? (
                <p className="text-center text-gray-500 py-8">No users found</p>
              ) : (
                <div className="space-y-2">
                  {/* Show real friends first */}
                  {realFriends.length > 0 && !friendSearchQuery && (
                    <>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Your Friends</p>
                      {realFriends.map((friend) => (
                        <div
                          key={friend.id}
                          className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10"
                        >
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center overflow-hidden">
                            {friend.avatar_url ? (
                              <img src={friend.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-white font-bold">{friend.username?.[0]?.toUpperCase()}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-medium">{friend.display_name || friend.username}</p>
                            <p className="text-gray-500 text-sm">@{friend.username}</p>
                          </div>
                          <button
                            onClick={() => handleStartRealChat(friend)}
                            className="px-4 py-2 bg-cyan-500 text-white rounded-lg text-sm font-medium"
                          >
                            Message
                          </button>
                        </div>
                      ))}
                      <div className="border-t border-white/10 my-4" />
                    </>
                  )}

                  {/* Search results */}
                  {friendSearchResults.map((searchUser) => {
                    const isFriend = realFriends.some(f => f.id === searchUser.id);
                    const isPending = pendingSent.some(p => p.friend_id === searchUser.id);
                    return (
                      <div
                        key={searchUser.id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10"
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center overflow-hidden">
                          {searchUser.avatar ? (
                            <img src={searchUser.avatar} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white font-bold">{searchUser.username?.[0]?.toUpperCase()}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">{searchUser.display_name || searchUser.username}</p>
                          <p className="text-gray-500 text-sm">@{searchUser.username}</p>
                        </div>
                        {isFriend ? (
                          <button
                            onClick={() => handleStartRealChat(searchUser)}
                            className="px-4 py-2 bg-cyan-500 text-white rounded-lg text-sm font-medium"
                          >
                            Message
                          </button>
                        ) : isPending || searchUser.requestSent ? (
                          <span className="px-4 py-2 bg-gray-600 text-gray-300 rounded-lg text-sm">Pending</span>
                        ) : (
                          <button
                            onClick={() => handleSendFriendRequest(searchUser.id)}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium"
                          >
                            Add Friend
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 📞 INCOMING CALL OVERLAY */}
      {incomingCall && callState === 'ringing' && (
        <div className="fixed inset-0 z-[100] bg-gradient-to-b from-gray-900 to-black flex flex-col items-center justify-center">
          {/* Caller Avatar */}
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-6 animate-pulse">
            {incomingCall.callerAvatar ? (
              <img src={incomingCall.callerAvatar} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              <span className="text-4xl font-bold text-white">{incomingCall.callerName?.[0]?.toUpperCase()}</span>
            )}
          </div>

          {/* Caller Name */}
          <h2 className="text-2xl font-bold text-white mb-2">{incomingCall.callerName}</h2>
          <p className="text-gray-400 mb-12">{incomingCall.type === 'video' ? 'Video Call' : 'Voice Call'}</p>

          {/* Accept / Decline */}
          <div className="flex gap-12">
            <button
              onClick={rejectCall}
              className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center"
            >
              <span className="text-2xl">✕</span>
            </button>
            <button
              onClick={acceptCall}
              className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center"
            >
              <span className="text-2xl">📞</span>
            </button>
          </div>
        </div>
      )}

      {/* 📞 ACTIVE CALL OVERLAY - Full-screen video/voice with translation */}
      {activeCall && (callState === 'calling' || callState === 'connected') && (
        <CallOverlay
          localStream={callLocalStream}
          remoteStream={callRemoteStream}
          onHangup={endCall}
          onToggleMute={toggleCallMute}
          onToggleVideo={toggleCallVideo}
          isMuted={callIsMuted}
          isVideoOff={!callIsVideoOn}
          callerName={activeCall.callerId === user?.id ? activeCall.receiverName : activeCall.callerName}
          callerAvatar={activeCall.callerId === user?.id ? activeCall.receiverAvatar : activeCall.callerAvatar}
          callDuration={callDuration}
          enableTranslation={true}
          myLanguage="en"
          theirLanguage="es"
        />
      )}
    </div>
  );
}

// 💬 REAL CHAT VIEW COMPONENT
function RealChatView({ friend, messages, onBack, onSend, onCall, onVideoCall, callDisabled }) {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (inputValue.trim()) {
      onSend(inputValue.trim());
      setInputValue('');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gjyl-darker">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gjyl-dark border-b border-white/10 safe-top">
        <button onClick={onBack} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
          ←
        </button>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center overflow-hidden">
          {friend.avatar_url ? (
            <img src={friend.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-bold">{friend.username?.[0]?.toUpperCase()}</span>
          )}
        </div>
        <div className="flex-1">
          <p className="text-white font-medium">{friend.display_name || friend.username}</p>
          <p className="text-gray-500 text-xs">@{friend.username}</p>
        </div>
        <button
          onClick={onCall}
          disabled={callDisabled}
          className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center disabled:opacity-50"
        >
          📞
        </button>
        <button
          onClick={onVideoCall}
          disabled={callDisabled}
          className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center disabled:opacity-50"
        >
          📹
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.sender_id === friend.id ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                msg.sender_id === friend.id
                  ? 'bg-white/10 text-white'
                  : 'bg-cyan-500 text-white'
              }`}
            >
              <p>{msg.content}</p>
              <p className="text-xs opacity-60 mt-1">
                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 bg-gjyl-dark border-t border-white/10 safe-bottom">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type a message..."
            className="flex-1 h-12 px-4 bg-zinc-800 text-white rounded-full placeholder-gray-500 focus:outline-none"
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            className="w-12 h-12 rounded-full bg-cyan-500 flex items-center justify-center disabled:opacity-50"
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}
